import { siteCapturesQuery } from '@/api/queries'
import { EventDrawer } from '@/components/drawers/EventDrawer'
import type { SalaIntent } from '@/lib/viewIntent'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useRouter } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { AnalysesBody } from './AnalysesBody'
import { ComposedBody } from './ComposedBody'
import { EventsByHourBody } from './EventsByHourBody'
import { PhotosBody } from './PhotosBody'
import { TimelineBody } from './TimelineBody'
import { t } from '@/lib/i18n'

interface SalaProps {
  intent: SalaIntent
  onBack: () => void
}

export function Sala({ intent, onBack }: SalaProps) {
  return (
    <div className="max-w-5xl mx-auto">
      <header className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('Back')}
          className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-muted text-foreground-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        </button>
        <h1 className="text-lg font-semibold">{titleFor(intent)}</h1>
      </header>
      <SalaBody intent={intent} />
      <SalaEventDrawer intent={intent} />
    </div>
  )
}

// Mounts the standard EventDrawer when `?event=<id>` is present on top of
// a sala. The site route mounts its own drawer when the sala is inactive;
// this one only runs while a sala is active, so we don't race with it.
//
// For the photos kind, the drawer's ←/→ navigation is overridden to walk
// the exact list of captures the sala is showing (via navList), not the
// full camera history — otherwise arrow keys would jump the user out of
// the curated set they asked to see.
function SalaEventDrawer({ intent }: { intent: SalaIntent }) {
  // For composed views the drawer still mounts (so a click on a photos
  // block opens the familiar event detail), but ←/→ falls back to the
  // server's prev/next instead of a per-block navList. A block-scoped
  // registry is a future refinement (TECHNICAL.md §4.13.8).
  if (intent.kind === 'composed') {
    const cs = intent.site_id
    if (!cs) return null
    return (
      <DrawerFromSearch
        siteId={cs}
        cameraId={intent.camera_id ?? null}
        navList={undefined}
      />
    )
  }
  const siteId = intent.site_id
  const cameraId = intent.camera_id ?? null
  const isPhotos = intent.kind === 'photos'
  // Re-issue the same capture query PhotosBody used; React Query
  // deduplicates by key, so this is a cache hit when the body is loaded.
  const capturesQ = useQuery({
    ...siteCapturesQuery(
      siteId,
      isPhotos ? (intent.date ?? null) : null,
      isPhotos ? cameraId : null,
      isPhotos ? (intent.event_ids ?? null) : null,
    ),
    enabled: isPhotos,
  })
  const navList: number[] | undefined = isPhotos
    ? capturesQ.data?.captures.map((c) => c.event_id)
    : undefined
  return <DrawerFromSearch siteId={siteId} cameraId={cameraId} navList={navList} />
}

function DrawerFromSearch({
  siteId,
  cameraId,
  navList,
}: {
  siteId: string
  cameraId: string | null
  navList: number[] | undefined
}) {
  const router = useRouter()
  const eventId = useLocation({
    select: (s) => {
      const raw = (s.search as { event?: unknown }).event
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw
      if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number(raw)
      return null
    },
  })
  if (eventId === null) return null
  const close = () =>
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: Record<string, unknown>) => {
        const { event: _drop, ...rest } = prev
        return rest
      },
    } as never)
  const goTo = (next: number) =>
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: Record<string, unknown>) => ({ ...prev, event: next }),
    } as never)
  return (
    <EventDrawer
      siteId={siteId}
      eventId={eventId}
      cameraId={cameraId}
      navList={navList}
      onClose={close}
      onNavigate={goTo}
    />
  )
}

function SalaBody({ intent }: { intent: SalaIntent }) {
  switch (intent.kind) {
    case 'photos':
      return <PhotosBody intent={intent} />
    case 'analyses':
      return <AnalysesBody intent={intent} />
    case 'timeline':
      return <TimelineBody intent={intent} />
    case 'events_by_hour':
      return <EventsByHourBody intent={intent} />
    case 'composed':
      return <ComposedBody intent={intent} />
  }
}

export function titleFor(intent: SalaIntent): string {
  const cam = intent.camera_id ? ` — ${intent.camera_id}` : ''
  const site = intent.site_id ?? '—'
  const when = rangeLabel(intent)

  switch (intent.kind) {
    case 'photos': {
      const curated =
        intent.event_ids && intent.event_ids.length > 0
          ? ` — ${t('{n} selected', { n: intent.event_ids.length })}`
          : ''
      return `${t('Photos')} — ${site}${cam}${when ? ` — ${when}` : ''}${curated}`
    }
    case 'analyses':
      return `${t('Analyses')} — ${site}${cam}${when ? ` — ${when}` : ''}`
    case 'timeline':
      return `${t('Timeline')} — ${site}${cam}${when ? ` — ${when}` : ''}`
    case 'events_by_hour':
      return `${t('Events by hour')} — ${site}${cam}${when ? ` — ${when}` : ''}`
    case 'composed':
      return intent.title ?? (intent.site_id ? `${t('Composed view')} — ${intent.site_id}` : t('Composed view'))
  }
}

function rangeLabel(intent: SalaIntent): string {
  if ('date' in intent && intent.date) return intent.date
  if ('date_from' in intent && intent.date_from && 'date_to' in intent && intent.date_to) {
    return `${intent.date_from}..${intent.date_to}`
  }
  return ''
}
